import React, { forwardRef, ReactNode, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { genericMemo, useMountEffect } from '@direct-frontend/stdlib';

import { cn } from '../../lib/css/className/className';
import { useOverlay } from '../../hooks/useOverlay/useOverlay';
import { TestProps } from '../../internal/types/testTypes';
import { useLayerContext } from '../../lib/layerManager/layerManager';

import { useRelativePositionForStaticTarget } from './hooks/useRelativePositionForStaticTarget/useRelativePositionForStaticTarget';
import { useDimmedTarget } from './hooks/useDimmedTarget/useDimmedTarget';

import './Scrim.css';

const cls = cn('Scrim');

// Насколько приглушаем заблокированный контент, пока Scrim активен
const DIMMED_OPACITY = 0.5;

export interface ScrimProps extends TestProps {
    /**
     * Дочерние элементы, которые Scrim оборачивает.
     */
    children: ReactNode;

    /**
     * Активен ли блокирующий слой.
     * При false Scrim рендерит только контент без оверлея.
     * @default false
     */
    isActive?: boolean;

    /**
     * Внешний контейнер, внутри которого нужно нарисовать блокирующий слой.
     * Если не передан, слой рисуется поверх собственных children.
     */
    targetRef?: React.RefObject<HTMLElement | null>;
}

interface ScrimRenderBaseProps {
    /**
     * Дочерние элементы, которые Scrim оборачивает.
     */
    children: ReactNode;
    /**
     * Активен ли блокирующий слой.
     */
    isActive: boolean;
    /**
     * Тестовый ID для отрисовки.
     */
    dataTestId: string;
    /**
     * Ссылка на внешний контейнер, внутри которого нужно нарисовать блокирующий слой.
     */
    forwardedRef: React.ForwardedRef<HTMLDivElement>;
}

interface PortalScrimProps extends ScrimRenderBaseProps {
    /**
     * Ссылка на внешний контейнер, внутри которого нужно нарисовать блокирующий слой.
     */
    targetRef: React.RefObject<HTMLElement | null>;
}

interface ScrimOverlayProps {
    /**
     * Активен ли блокирующий слой.
     */
    isActive: boolean;
    /**
     * Тестовый ID для отрисовки.
     */
    dataTestId: string;
}

const useScrimOverlayLayer = (isActive: boolean) => {
    const layerManager = useLayerContext();
    const overlayRef = React.useRef<HTMLDivElement>(null);
    const scrimLayerId = React.useId();

    // Необходимо, чтобы Scrim был под контекстными попапами, но покрывал остальной контент форм
    const overlayZIndex = layerManager.zIndexForContextPopups - 1;

    const { overlayProps } = useOverlay(
        {
            isOpen: isActive,
            isLayer: false,
            isModal: false,
            isDismissable: false,
            isAddedToLayerManager: true,
            zIndex: overlayZIndex,
            parentLayerId: layerManager.id,
            id: scrimLayerId,
        },
        overlayRef,
    );

    return { overlayRef, overlayProps, overlayZIndex };
};

const ScrimOverlay = ({ isActive, dataTestId }: ScrimOverlayProps) => {
    const { overlayRef, overlayProps, overlayZIndex } = useScrimOverlayLayer(isActive);

    if (!isActive) {
        return null;
    }

    return (
        <div
            {...overlayProps}
            ref={overlayRef}
            className={cls('overlay')}
            style={{ zIndex: overlayZIndex }}
            data-testid={`${dataTestId}.Overlay`}
        />
    );
};

const SimpleScrim = ({ children, isActive, dataTestId, forwardedRef }: ScrimRenderBaseProps) => {
    const handleFocusCapture = useCallback(
        (event: React.FocusEvent<HTMLDivElement>) => {
            if (!isActive) {
                return;
            }

            event.stopPropagation();

            if (typeof document === 'undefined') {
                return;
            }

            const activeElement = document.activeElement;

            if (activeElement instanceof HTMLElement) {
                activeElement.blur();
            }
        },
        [isActive],
    );

    return (
        <div
            ref={forwardedRef}
            className={cls({
                active: isActive,
            })}
            data-testid={dataTestId}
            aria-hidden={isActive || undefined}
            onFocusCapture={handleFocusCapture}
        >
            {children}
            {isActive && <ScrimOverlay isActive={isActive} dataTestId={dataTestId} />}
        </div>
    );
};

const PortalScrim = ({ children, isActive, targetRef, dataTestId }: PortalScrimProps) => {
    // eslint-disable-next-line react-hooks/refs -- читаем ref только для определения контейнера портала
    const targetElement = isActive ? targetRef.current : null;
    const overlayTarget = targetElement ? targetElement : null;

    // На первый рендер с активным состоянием рефа, скорее всего, не будет
    const [_, setIsInitialized] = useState(false);
    // Поэтому на маунт нужно вызвать ререндер, чтобы ref появился
    useMountEffect(() => {
        if (!targetElement && targetRef.current && isActive) {
            requestAnimationFrame(() => {
                setIsInitialized(true);
            });
        }
    });

    // Программное отключение фокуса на элементах в targetRef
    React.useEffect(() => {
        if (!targetElement || !isActive || typeof document === 'undefined') {
            return undefined;
        }

        const blurFocusInsideTarget = (event: FocusEvent) => {
            const eventTarget = event.target;

            if (!(eventTarget instanceof HTMLElement)) {
                return;
            }

            if (!targetElement.contains(eventTarget)) {
                return;
            }

            eventTarget.blur();
        };

        document.addEventListener('focusin', blurFocusInsideTarget, true);

        return () => {
            document.removeEventListener('focusin', blurFocusInsideTarget, true);
        };
    }, [isActive, targetElement]);

    useDimmedTarget(targetElement, isActive, DIMMED_OPACITY);

    useRelativePositionForStaticTarget(overlayTarget);

    return (
        <>
            {children}
            {overlayTarget && createPortal(<ScrimOverlay isActive={isActive} dataTestId={dataTestId} />, overlayTarget)}
        </>
    );
};

/**
 * Компонент для приглушения и блокировки взаимодействия с контентом.
 */
export const Scrim = genericMemo(
    forwardRef<HTMLDivElement, ScrimProps>(function Scrim(props, forwardedRef) {
        const { children, isActive = false, targetRef, 'data-testid': dataTestId = 'Scrim' } = props;

        const layerManager = useLayerContext();

        // В случае, если контент отрисовывается внутри модалки, используем контейнер модалки для отрисовки Scrim
        const ref = targetRef ? targetRef : layerManager.containerContentRef;

        if (ref) {
            return (
                <PortalScrim
                    children={children}
                    isActive={isActive}
                    targetRef={ref}
                    dataTestId={dataTestId}
                    forwardedRef={forwardedRef}
                />
            );
        }

        return (
            <SimpleScrim children={children} isActive={isActive} dataTestId={dataTestId} forwardedRef={forwardedRef} />
        );
    }),
);
